import { useMutation } from '@tanstack/react-query';
import { apiClient, isNetworkError } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import toast from '@/lib/toast';
import type { Conversation, AnalysisResponse } from '@/lib/types';

export function useAnalyzeConversation() {
  return useMutation({
    mutationFn: (conversation: Conversation) =>
      apiClient.analyzeConversation(conversation),
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401, 403)
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
        return false;
      }
      // Retry up to 2 times for network errors and 5xx errors
      if (isNetworkError(error) || (error instanceof Error && error.message.includes('500'))) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    onSuccess: (data: AnalysisResponse) => {
      console.log('Analysis complete:', data);
      toast.success('Conversation analyzed', {
        description: 'Sentiment and questions are ready.',
      });
    },
    onError: (error: Error) => {
      console.error('Analysis failed:', error);

      // Check if it's an auth error
      if (error.message.includes('401') || error.message.includes('403') || error.message.includes('Unauthorized')) {
        // Build proper OAuth authorize URL (same as admin app)
        const authDomain = import.meta.env.VITE_AUTH_URL || 'https://auth.crewflow.com';
        const apiDomain = import.meta.env.VITE_API_URL || 'https://api.crewflow.com';

        // Build OAuth URL with proper parameters
        const params = new URLSearchParams({
          client_id: 'crewflow-web',
          redirect_uri: `${apiDomain}/auth/callback`,
          response_type: 'code',
          scope: 'openid email profile',
          state: window.location.href, // Return to current plugin page
        });

        const authUrl = `${authDomain}/authorize?${params.toString()}`;

        // Update user store to show auth prompt
        useUserStore.getState().setUserStatus(null, true, authUrl);
        toast.error('Authentication required', {
          description: 'Please sign in to use Crewflow.',
        });
      } else {
        toast.error('Failed to analyze conversation', {
          description: error.message || 'Please try again.',
        });
      }
    },
  });
}
